// Shared types + labels for the Asset Inventory page.

import {
  ASSET_CATEGORIES,
  assetTypesForCategory,
  type AssetCategory,
} from '@deepguide-ai/types'

export const ASSET_TYPE_LABELS: Record<string, string> = {
  COMPUTE: 'Compute',
  STORAGE: 'Storage',
  DATABASE: 'Database',
  NETWORK: 'Network',
  IDENTITY: 'Identity',
  SAAS: 'SaaS',
  CONTAINER: 'Container',
  REPOSITORY: 'Repository',
  DEVICE: 'Device',
  FACILITY: 'Facility',
  DATASET: 'Dataset',
  APPLICATION: 'Application',
  AI_SYSTEM: 'AI system',
  MODEL: 'Model',
  MCP_SERVER: 'MCP server',
  PROMPT: 'Prompt',
  AGENT: 'Agent',
  TOOL: 'Tool',
  GUARDRAIL: 'Guardrail',
  OTHER: 'Other',
}

export const ASSET_CATEGORY_LABELS: Record<AssetCategory, string> = {
  devices: 'Devices',
  software: 'Software',
  ai_system: 'AI System',
  cloud: 'Cloud',
  other: 'Other',
}

/** Type options grouped by register bucket, for grouped <Select> lists. */
export const ASSET_TYPE_GROUPS = ASSET_CATEGORIES.map((category) => ({
  category,
  label: ASSET_CATEGORY_LABELS[category],
  types: assetTypesForCategory(category),
}))

/** SelectLabel styling for the grouped Type dropdowns — tiny uppercase
 * muted headers so groups read as headers, not options. */
export const ASSET_TYPE_GROUP_LABEL_CLASS =
  'text-text-placeholder text-[10px] font-semibold tracking-[0.06em] uppercase'

export const CLASSIFICATION_LABELS: Record<string, string> = {
  PUBLIC: 'Public',
  INTERNAL: 'Internal',
  CONFIDENTIAL: 'Confidential',
  RESTRICTED: 'Restricted',
}

export const CRITICALITY_LABELS: Record<string, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

export type AssetTypeFilter =
  | 'all'
  | 'COMPUTE'
  | 'STORAGE'
  | 'DATABASE'
  | 'NETWORK'
  | 'IDENTITY'
  | 'SAAS'
  | 'CONTAINER'
  | 'REPOSITORY'
  | 'DEVICE'
  | 'FACILITY'
  | 'DATASET'
  | 'APPLICATION'
  | 'AI_SYSTEM'
  | 'OTHER'

export type CriticalityFilter = 'all' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
export type ClassificationFilter =
  | 'all'
  | 'PUBLIC'
  | 'INTERNAL'
  | 'CONFIDENTIAL'
  | 'RESTRICTED'
export type AssetEphiFilter = 'all' | 'ephi_only' | 'phi_free'

/** #1423: resource-scope filter (derived from SCOPE exceptions). */
export type ScopeFilter = 'all' | 'in_scope' | 'out_of_scope'

/**
 * Classification = data-SENSITIVITY ramp (green → gray → purple → red).
 * Deliberately a different family from criticality's heat ramp so a
 * "Confidential + High" asset doesn't render two identical amber chips.
 */
export function classificationColor(classification: string): string {
  switch (classification) {
    case 'RESTRICTED':
      return 'bg-status-red-bg text-status-red'
    case 'CONFIDENTIAL':
      return 'bg-status-purple-bg text-status-purple'
    case 'INTERNAL':
      return 'bg-status-gray-bg text-status-gray'
    case 'PUBLIC':
      return 'bg-status-green-bg text-status-green'
    default:
      return 'bg-status-gray-bg text-status-gray'
  }
}

/** Criticality = severity heat ramp (green → blue → amber → red). */
export function criticalityColor(criticality: string): string {
  switch (criticality) {
    case 'CRITICAL':
      return 'bg-status-red-bg text-status-red'
    case 'HIGH':
      return 'bg-status-orange-bg text-status-orange'
    case 'MEDIUM':
      return 'bg-status-blue-bg text-status-blue'
    case 'LOW':
      return 'bg-status-green-bg text-status-green'
    default:
      return 'bg-status-gray-bg text-status-gray'
  }
}

/** True when an asset came from a sync provider (vs. manual entry). */
export function isSyncedAsset(item: { provider?: string | null }): boolean {
  return !!item.provider && item.provider !== 'manual'
}
